import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet } from "react-router";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import {
  RouteTabs,
  type RouteTabItem,
} from "../../../components/layout/routeTabs.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectIsUsingSSO } from "../../company/companySlice.js";
import { settingsSections } from "../settingsSections.js";
import { SignInSettingsTab } from "./signInSettingsTab.js";
import { TeammateGroupsSettingsSection } from "./teammateGroupsSettingsSection.js";

export const TeammatesSettingsSection: FC = () => {
  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );
  const isUsingSSO = useAppSelector(selectIsUsingSSO);

  const tabs: RouteTabItem[] = [
    { label: "Teammates", to: "." },
    ...(hasTeamPerformanceTab
      ? [{ label: "Teammate groups", to: "groups" }]
      : []),
    ...(!isUsingSSO ? [{ label: "Sign-in settings", to: "sign-in" }] : []),
  ];

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={settingsSections.teammates.label}
          tabs={
            <RouteTabs ariaLabel="Teammate settings sections" tabs={tabs} />
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet />
      </PageBody>
    </TabbedPageLayout>
  );
};

// The tabs' feature gates, applied at render time now that the routes are
// static (see SettingsSectionGuard for the rationale).

export const TeammateGroupsSettingsRoute: FC = () => {
  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );
  return hasTeamPerformanceTab ? (
    <TeammateGroupsSettingsSection />
  ) : (
    <Navigate to={appPaths.settingsTeammates.build()} replace />
  );
};

export const SignInSettingsRoute: FC = () => {
  const isUsingSSO = useAppSelector(selectIsUsingSSO);
  return isUsingSSO ? (
    <Navigate to={appPaths.settingsTeammates.build()} replace />
  ) : (
    <SignInSettingsTab />
  );
};
